import { faDownload } from "@fortawesome/pro-light-svg-icons";
import type { Theme } from "@mui/system";
import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import type { ComicDrawerProps } from "@vapor/react-contrib-tax-compliance/ComicDrawer/ComicDrawer";
import { VaporToolbar } from "@vapor/react-custom";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { VaporIcon } from "@vapor/react-icons";
import { Box, Button, useMediaQuery } from "@vapor/react-material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SyntaxHighlighter from "react-syntax-highlighter";
import { downloadFile } from "../../../../core/utils/downloadFile";
import { base64ToArrayBuffer } from "../../../utils/file";

export interface SogeiOutcomeDrawerParams {
    content: string;
    loading: boolean;
}

export const useSogeiOutcomeDrawer = (
    params: SogeiOutcomeDrawerParams,
    setParams: (params: SogeiOutcomeDrawerParams) => void,
): ComicDrawerProps | undefined => {
    const { t } = useTranslation();
    const isXlScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.up(theme.breakpoints.values.xl),
    );

    return useMemo(() => {
        if (!params.loading && !params.content) return undefined;

        return {
            content: (
                <LoadingWrapper
                    loading={params.loading}
                    spinnerProps={{
                        loadingText: t(
                            "components.loading_wrapper.loading_message",
                        ),
                    }}
                >
                    <SyntaxHighlighter language="text">
                        {params.content}
                    </SyntaxHighlighter>
                </LoadingWrapper>
            ),
            headerTitle: t(
                "views.home.components.tax_submission_preview_sogei.validation",
            ),
            onClickCloseButton: () => {
                setParams({ content: "", loading: false });
            },
            open: params.loading || !!params.content,
            width: isXlScreen ? "45%" : "65%",
            footer: (
                <Box sx={{ ml: -4 }}>
                    <VaporToolbar
                        contentRight={[
                            <Button
                                size="large"
                                variant="contained"
                                onClick={() =>
                                    downloadFile(
                                        base64ToArrayBuffer(
                                            btoa(params.content),
                                        ),
                                        "esito_sogei.wri",
                                        "text/plain",
                                    )
                                }
                            >
                                <VaporIcon icon={faDownload} color="inverse" />
                                <Typography style={{ marginLeft: "10px" }}>
                                    {t(
                                        "views.home.components.tax_submission_download_sogei_error.download_sogei_error",
                                    )}
                                </Typography>
                            </Button>,
                        ]}
                        size="large"
                        variant="regular"
                    />
                </Box>
            ),
        };
    }, [params, setParams, t, isXlScreen]);
};
